/**
 * The Admin's ride grid, report catalogue and global audit trail (W9-RIDES-REPORTS-AUDIT, D472, ADR-0090; DevMobility A-11,
 * C-008, I-025, C-007, H-042, H-043, H-044, I-051, A-071, I-102; X-ray A-11, A-14, P-07) — framework-neutral view-models.
 *
 *  - **Corridas › Grade** (`rides_grid`): the rides of a market over a period by status, category and payment method, with a
 *    BATCH CANCEL (never delete) of the rides the operator ticks, one closed reason code for the batch.
 *  - **Relatórios › Catálogo** (`report_catalog`): the closed catalogue of reports, a preview and a CSV download.
 *  - **Auditoria › Trilha** (`audit_trail`): one filterable trail over the audit sources the operator's scope reaches.
 *
 * The console only presents: the server authorizes every call at the market (or country) asked for and refuses what the
 * grant does not cover. Neutral: no country, currency, zone or language is named here.
 */
import { OPS_CANCEL_REASON_CODES, type AuditSourceView, type RideGridRowView, type RideGridStatusView } from '@vello/api-client';
import { grantsAllow, type KnownGrants } from './console-access.js';

export type RidesReportsTone = 'neutral' | 'success' | 'warning' | 'danger';

// ---- authority (display only — the server re-authorizes every call) ------------------------------------------------

export interface RidesReportsAuthority {
  readonly mayList: boolean;
  readonly mayCancel: boolean;
  readonly mayViewReports: boolean;
  readonly mayExport: boolean;
  readonly mayAudit: boolean;
}

export function ridesReportsAuthority(grants: KnownGrants): RidesReportsAuthority {
  if (grants === undefined) return { mayList: false, mayCancel: false, mayViewReports: false, mayExport: false, mayAudit: false };
  return {
    mayList: grantsAllow(grants, { permission: 'ride:view', target: 'GEO' }),
    mayCancel: grantsAllow(grants, { permission: 'ride:cancel_batch', target: 'GEO' }),
    mayViewReports: grantsAllow(grants, { permission: 'report:view', target: 'GEO' }),
    mayExport: grantsAllow(grants, { permission: 'report:export', target: 'GEO' }),
    mayAudit: grantsAllow(grants, { permission: 'audit:inspect', target: 'GEO' }),
  };
}

// ---- the ride grid -------------------------------------------------------------------------------------------------

export const GRID_STATUSES: readonly RideGridStatusView[] = ['requested', 'searching', 'assigned', 'en_route', 'arrived', 'in_progress', 'completed', 'cancelled', 'no_driver'];
/** The payment methods a ride's snapshot carries (the neutral tokens of `payment.allowedMethods`): the grid's "Forma de pagamento" options. */
export const GRID_PAYMENT_METHODS: readonly string[] = ['cash', 'card', 'instant_payment'];

/**
 * D854 (L7a-025): the choices of a grid filter that is a select. The value already chosen (it can come from the URL) always stays
 * selectable, so a link with `?category=x` never shows an empty box; the others keep their order and appear once.
 */
export function gridFilterChoices(options: readonly string[], current: string): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of current.trim() === '' ? options : [...options, current.trim()]) {
    if (code.trim() !== '' && !seen.has(code)) {
      seen.add(code);
      out.push(code);
    }
  }
  return out;
}

/** The most rides one batch may name (mirrors the server's bound). */
export const BATCH_MAX = 50;
/** The closed reason codes a batch cancel carries (the single operator cancel's vocabulary). */
export const BATCH_REASONS = OPS_CANCEL_REASON_CODES;

export function gridStatusTone(status: string): RidesReportsTone {
  switch (status) {
    case 'completed':
      return 'success';
    case 'cancelled':
    case 'no_driver':
      return 'danger';
    case 'requested':
    case 'searching':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** The rides of a page the batch action may name (a terminal ride cannot be cancelled). */
export function cancellableRideIds(rows: readonly RideGridRowView[]): readonly string[] {
  return rows.filter((r) => r.cancellable).map((r) => r.rideId);
}

/** Tick or untick a ride, never growing past {@link BATCH_MAX}. */
export function toggleRide(selected: readonly string[], rideId: string): readonly string[] {
  if (selected.includes(rideId)) return selected.filter((id) => id !== rideId);
  return selected.length >= BATCH_MAX ? selected : [...selected, rideId];
}

/** Tick every cancellable ride of the page (up to the bound), or clear when all of them are already ticked. */
export function toggleAllRides(selected: readonly string[], rows: readonly RideGridRowView[]): readonly string[] {
  const ids = cancellableRideIds(rows).slice(0, BATCH_MAX);
  return ids.length > 0 && ids.every((id) => selected.includes(id)) ? [] : ids;
}

/** Keep only the ticked rides that are still on the grid and still cancellable (after a reload). */
export function pruneSelection(selected: readonly string[], rows: readonly RideGridRowView[]): readonly string[] {
  const live = new Set(cancellableRideIds(rows));
  return selected.filter((id) => live.has(id));
}

// ---- the report catalogue ------------------------------------------------------------------------------------------

export type FilterControl =
  | { readonly kind: 'select'; readonly options: readonly string[] }
  | { readonly kind: 'text' }
  | { readonly kind: 'number'; readonly min: number; readonly max: number };

/** How each filter key a report may declare is entered. A key not listed is not offered (the server would refuse it anyway). */
export const REPORT_FILTER_CONTROLS: Readonly<Record<string, FilterControl>> = {
  status: { kind: 'select', options: GRID_STATUSES },
  category: { kind: 'text' },
  payment: { kind: 'text' },
  cancelledBy: { kind: 'select', options: ['rider', 'driver', 'ops', 'desk', 'system'] },
  stage: { kind: 'select', options: ['BEFORE_ASSIGNMENT', 'AFTER_ASSIGNMENT'] },
  offerStatus: { kind: 'select', options: ['created', 'sent', 'viewed', 'accepted', 'rejected', 'expired', 'superseded', 'cancelled'] },
  inactiveDays: { kind: 'number', min: 7, max: 365 },
  direction: { kind: 'select', options: ['UP', 'DOWN'] },
};

/** The filters the operator filled in, as the flat object the API takes (empty entries dropped). */
export function cleanFilters(values: Readonly<Record<string, string>>, declared: readonly string[]): Readonly<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const key of declared) {
    const v = values[key]?.trim();
    if (v !== undefined && v !== '') out[key] = v;
  }
  return out;
}

/** The CSV delimiter a language's spreadsheet expects: `pt`/`es`/`it` locales split on `;`, the rest on `,`. */
export function defaultDelimiter(lang: string): 'comma' | 'semicolon' {
  return lang === 'pt' || lang === 'es' || lang === 'it' ? 'semicolon' : 'comma';
}

// ---- the audit trail -----------------------------------------------------------------------------------------------

export const MARKET_AUDIT_SOURCES: readonly AuditSourceView[] = ['AUDIT_LOG', 'DISPATCH'];
export const PLATFORM_AUDIT_SOURCES: readonly AuditSourceView[] = ['OPERATOR_ADMIN', 'CONFIG_GOVERNANCE', 'ACCOUNT_ACTION'];

/** A compact `key: value` line of an entry's detail (ids, codes, counts — the server already stripped free text). */
export function detailLine(detail: Readonly<Record<string, unknown>> | undefined): string {
  if (detail === undefined) return '';
  return Object.entries(detail)
    .map(([k, v]) => `${k}: ${Array.isArray(v) ? `${v.length}` : String(v)}`)
    .join(' · ');
}

// ---- refusals ------------------------------------------------------------------------------------------------------

